import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  FormGroup, 
  FormControlLabel, 
  Checkbox, 
  Button, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  useTheme,
  styled,
  alpha
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageBackground from '../components/PageBackground';
import { ANIMATION_PRESETS, DURATION, EASING } from '../theme/animations';

// Define checkbox options
const checkboxOptions = [
  "Backing in XX",
  "Lack Comps (same building)",
  "Lack Comps - time",
  "Lack Comps - Character",
  "<100% Complete",
  "No Influences",
  "Influences noted",
  "Acc. Differences",
  "Close to amenities",
  "Property Conforms",
  "No concerns"
];

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.92),
  backdropFilter: 'blur(10px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  transition: `all ${DURATION.normal} ${EASING.easeOut}`,
  overflow: 'hidden',
  '&:hover': {
    boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
    transform: 'translateY(-3px)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.2, 3),
  borderRadius: 8,
  fontWeight: 600,
  textTransform: 'none',
  transition: `all ${DURATION.normal} ${EASING.easeOut}`,
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: `0 6px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '12px !important',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  transition: `all ${DURATION.normal} ${EASING.easeOut}`,
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: `${theme.spacing(2)}px 0`,
    boxShadow: `0 6px 15px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
  '&:hover': {
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.light, 0.08),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1, 0),
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transition: `all ${DURATION.normal} ${EASING.easeInOut}`,
  },
  '&.Mui-expanded': {
    '& .MuiAccordionSummary-expandIconWrapper': {
      transform: 'rotate(180deg)',
    },
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  transition: `all ${DURATION.fast} ${EASING.easeInOut}`,
  borderRadius: 8,
  padding: '2px 4px',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.light, 0.1),
    transform: 'translateX(4px)',
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
  },
  '& .MuiCheckbox-root': {
    color: theme.palette.primary.main,
    padding: '2px',
  },
}));

const AppraisalCRPage: React.FC = () => {
  const theme = useTheme();
  const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>(
    checkboxOptions.reduce((acc, option) => ({ ...acc, [option]: false }), {})
  );
  const [expanded, setExpanded] = useState<string | false>('panel1');

  // Handle checkbox change
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxState({
      ...checkboxState,
      [event.target.name]: event.target.checked,
    });
  };

  // Handle accordion change
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Handle submit
  const handleSubmit = () => {
    console.log('Submitted values:', checkboxState);
    // Add submission logic here
  };

  // Split checkbox options into 3 columns
  const columnSize = Math.ceil(checkboxOptions.length / 3);
  const column1 = checkboxOptions.slice(0, columnSize);
  const column2 = checkboxOptions.slice(columnSize, columnSize * 2);
  const column3 = checkboxOptions.slice(columnSize * 2);

  return (
    <PageBackground>
      <Typography variant="h4" sx={{ 
        mb: 3, 
        color: theme.palette.text.primary, 
        ...ANIMATION_PRESETS.fadeIn 
      }}>
        Appraisal CR
      </Typography>

      <StyledPaper elevation={3} sx={ANIMATION_PRESETS.fadeIn}>
        {/* Checkboxes in 3 columns */}
        <Grid container spacing={1}>
          {/* Column 1 */}
          <Grid item xs={12} md={4}>
            <FormGroup>
              {column1.map((option) => (
                <StyledFormControlLabel
                  key={option}
                  control={
                    <Checkbox 
                      checked={checkboxState[option]} 
                      onChange={handleCheckboxChange} 
                      name={option}
                      color="primary"
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
          </Grid>

          {/* Column 2 */}
          <Grid item xs={12} md={4}>
            <FormGroup>
              {column2.map((option) => (
                <StyledFormControlLabel
                  key={option}
                  control={
                    <Checkbox 
                      checked={checkboxState[option]} 
                      onChange={handleCheckboxChange} 
                      name={option}
                      color="primary"
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
          </Grid>

          {/* Column 3 */}
          <Grid item xs={12} md={4}>
            <FormGroup>
              {column3.map((option) => (
                <StyledFormControlLabel
                  key={option}
                  control={
                    <Checkbox 
                      checked={checkboxState[option]} 
                      onChange={handleCheckboxChange} 
                      name={option}
                      color="primary"
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
          </Grid>
        </Grid>

        {/* Submit Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          my: 4,
          ...ANIMATION_PRESETS.fadeIn
        }}>
          <StyledButton 
            variant="contained" 
            color="primary" 
            size="large" 
            onClick={handleSubmit}
          >
            Submit
          </StyledButton>
        </Box>

        {/* Accordion Sections */}
        <Box sx={{ mt: 4 }}>
          <StyledAccordion 
            expanded={expanded === 'panel1'} 
            onChange={handleAccordionChange('panel1')}
            sx={ANIMATION_PRESETS.slideInUp}
          >
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>
                Unedited
              </Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <Typography>
                This is the unedited content section.
              </Typography>
            </StyledAccordionDetails>
          </StyledAccordion>

          <StyledAccordion 
            expanded={expanded === 'panel2'} 
            onChange={handleAccordionChange('panel2')}
            sx={ANIMATION_PRESETS.slideInUp}
          >
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>
                AI Enhanced
              </Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <Typography>
                This is the AI enhanced content section.
              </Typography>
            </StyledAccordionDetails>
          </StyledAccordion>
        </Box>
      </StyledPaper>
    </PageBackground>
  );
};

export default AppraisalCRPage; 